import React, { ReactNode, useMemo } from 'react';

import { CfFlowResponse, CfFlowScreen } from '../api/cloudflareFlowApi';
import CloudflareAccountStep from './CloudflareAccountStep';
import CloudflareNameserverStep from './CloudflareNameserverStep';
import CloudflareProgressStep from './CloudflareProgressStep';
import { CfVerificationRetry } from './cfVerificationRetry';
import CloudflarePropagationStep from './CloudflarePropagationStep';
import CloudflareFlowFinished from './CloudflareFlowFinished';
import { CfFlowEventSubjectProvider } from '../useCfFlowEventTracker';
import type { CfNexusOpening } from '../cfNexusOpening';
import { cfEventSubjectOf } from '../cfEventProperties';

export type CfDelegatedScreen = 'blocked' | 'token' | 'reconnect' | 'abandoned';

export type CloudflareFlowScreenProps = {
  flow: CfFlowResponse;
  screen?: CfFlowScreen;
  slots: Readonly<Record<CfDelegatedScreen, ReactNode>>;
  isCheckingNameservers?: boolean;
  verificationRetry?: CfVerificationRetry;
  isConfirmingAccount?: boolean;
  onCheckPropagation?: (options: { inBackground: boolean }) => Promise<boolean>;
  onConfirmAccount?: () => void;
  onConfirmNameservers?: () => void;
  onOpenNexus?: (opening: CfNexusOpening) => void;
  onRetrySetup?: () => void;
  onRetryVerification?: () => void;
  onGoBack?: () => void;
  onFinish?: () => void;
};

const CloudflareFlowScreen = ({
  flow,
  screen = flow.screen,
  slots,
  isCheckingNameservers = false,
  verificationRetry = 'none',
  isConfirmingAccount = false,
  onCheckPropagation,
  onConfirmAccount,
  onConfirmNameservers,
  onOpenNexus,
  onRetrySetup,
  onRetryVerification,
  onGoBack,
  onFinish,
}: CloudflareFlowScreenProps) => {
  const subject = useMemo(
    () => cfEventSubjectOf(flow),
    // The flow object is replaced on every poll; only what the subject reads should move it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flow.domain, flow.status, flow.subCase, flow.entrySource],
  );

  const watched = (
    <CloudflareProgressStep
      flow={flow}
      verificationRetry={verificationRetry}
      onGoBack={onGoBack}
      onOpenNexus={onOpenNexus}
      onRetrySetup={onRetrySetup}
      onRetryVerification={onRetryVerification}
    />
  );

  /** A total `Record` over `CfFlowScreen`, so a new screen fails the build instead of rendering blank. */
  const byScreen: Readonly<Record<CfFlowScreen, () => ReactNode>> = {
    progress: () => watched,
    error: () => watched,
    propagation: () => (
      <CloudflarePropagationStep
        domain={flow.domain}
        lastCheckedAt={flow.lastCheckedAt}
        nameservers={flow.nameservers ?? []}
        onCheck={onCheckPropagation}
      />
    ),
    success: () => <CloudflareFlowFinished onFinish={onFinish} />,
    cf_account: () => (
      <CloudflareAccountStep
        domain={flow.domain}
        isConfirming={isConfirmingAccount}
        onConfirm={onConfirmAccount}
        onGoBack={onGoBack}
      />
    ),
    nameservers: () => (
      <CloudflareNameserverStep
        domain={flow.domain}
        nameservers={flow.nameservers ?? []}
        isChecking={isCheckingNameservers}
        onConfirm={onConfirmNameservers}
        onGoBack={onGoBack}
      />
    ),
    blocked: () => slots.blocked,
    token: () => slots.token,
    reconnect: () => slots.reconnect,
    abandoned: () => slots.abandoned,
  };

  return <CfFlowEventSubjectProvider value={subject}>{byScreen[screen]()}</CfFlowEventSubjectProvider>;
};

export default CloudflareFlowScreen;
